import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
	ClassicEditor,
	Essentials,
	Heading,
	Bold, Italic, Underline, Strikethrough,
	FontColor, FontBackgroundColor,
	List,
	Link,
	Image, ImageCaption, ImageStyle, ImageToolbar, ImageUpload,
	BlockQuote,
	Paragraph,
} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import Layout from '../../Global/Layout';
import DataTable from '../../Global/DataTable';
import { showConfirm, showDeleteConfirm, showError, showSuccess } from '../../../utils/sweetAlertConfig';
import useBodyScrollLock from '../../../hooks/useBodyScrollLock';
import { API_CONFIG, ICON_ACTION, LOTTO_TYPE } from '../../../contexts/constants';
import { formatDateTimeTable } from '../../../utils/dateFormatter';
import { CustomUploadAdapterPlugin } from '../../../utils/CKEditorUploadAdapter';

const Manage = ({ onLogout, userRole }) => {
	const [rules, setRules] = useState([]);
	const [loading, setLoading] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [totalItems, setTotalItems] = useState(0);
	const [showModal, setShowModal] = useState(false);
	const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
	const [currentRule, setCurrentRule] = useState(null);
	const [showSearchForm, setShowSearchForm] = useState(false);
	const [searchFilters, setSearchFilters] = useState({
		title: '',
		statusFilters: {
			active: true,
			inactive: true
		}
	});
	const [appliedFilters, setAppliedFilters] = useState({
		title: '',
		statusFilters: {
			active: true,
			inactive: true
		}
	});
	const [formData, setFormData] = useState({
		title: '',
		detail: '',
    	type: '',
		status: true
	});

	// Lock body scroll when modal is open
	useBodyScrollLock(showModal);

	const handleApiError = useCallback((error) => {
		let errorMessage = 'ไม่สามารถโหลดข้อมูลได้';

		if (error.response) {
			errorMessage = error.response.data?.message || `เกิดข้อผิดพลาด (${error.response.status})`;

			if (error.response.status === 401 || error.response.status === 403) {
				errorMessage = error.response.data?.message || 'กรุณาเข้าสู่ระบบใหม่';
				showError('เซสชันหมดอายุ', errorMessage);
				setTimeout(() => {
					localStorage.removeItem('token');
					onLogout();
				}, 1500);
				return;
			}
		} else if (error.request) {
			errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้';
		}

		showError('เกิดข้อผิดพลาด', errorMessage);
	}, [onLogout]);

	const fetchRules = useCallback(async () => {
		try {
			setLoading(true);

			// Build query parameters
			const params = new URLSearchParams({
				page: currentPage,
				pageSize: pageSize
			});

			// Add search filters
			if (appliedFilters.title) {
				params.append('title', appliedFilters.title);
			}

			// Determine status filter
			const { active, inactive } = appliedFilters.statusFilters;
			if (active && !inactive) {
				params.append('status', 'active');
			} else if (!active && inactive) {
				params.append('status', 'inactive');
			}
			// If both or neither are checked, don't add status filter

			const response = await axios.get(`${API_CONFIG.BASE_URL}/rules?${params.toString()}`, {
				headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
			});

			if (response.data.success && Array.isArray(response.data.data)) {
				setRules(response.data.data);
				setTotalItems(response.data.total || 0);
			} else {
				console.error('Invalid rules data format');
				setRules([]);
				setTotalItems(0);
			}
		} catch (error) {
			console.error('Error fetching rules:', error);
			handleApiError(error);
			setRules([]);
			setTotalItems(0);
		} finally {
			setLoading(false);
		}
	}, [currentPage, pageSize, appliedFilters, handleApiError]);

	useEffect(() => {
		fetchRules();
	}, [fetchRules]);

	if (!['sysadmin', 'owner', 'admin'].includes(userRole)) {
		window.location.href = '/forbidden';
		return null;
	}

	const handleAdd = () => {
		setModalMode('add');
		setFormData({
			title: '',
			detail: '',
      		type: '',
			status: true
		});
		setCurrentRule(null);
		setShowModal(true);
	};

	const handleEdit = (rule) => {
		setModalMode('edit');
		setFormData({
			title: rule.title || '',
			detail: rule.detail || '',
      	type: rule.type || '',
			status: rule.status || false
		});
		setCurrentRule(rule);
		setShowModal(true);
	};

	const handleDelete = async (rule) => {
		const result = await showDeleteConfirm({
			title: 'ยืนยันการลบ?',
			text: `คุณต้ต้องการลบ "${rule.title}" หรือไม่?`
		});

		if (result.isConfirmed) {
			try {
				await axios.delete(`${API_CONFIG.BASE_URL}/rules/${rule.id}`, {
					headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
				});

				showSuccess('สำเร็จ', 'ลบข้อมูลเรียบร้อยแล้ว');
				fetchRules();
			} catch (error) {
				console.error('Error deleting rule:', error);
				handleApiError(error);
			}
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		// Validate
		if (!formData.title.trim()) {
			showError('กรุณากรอกข้อมูล', 'กรุณากรอกหัวข้อ');
			return;
		}

		if (!formData.type) {
			showError('กรุณากรอกข้อมูล', 'กรุณาเลือกประเภท');
			return;
		}

		if (!formData.detail.trim() || formData.detail === '<p>&nbsp;</p>' || formData.detail === '<p></p>') {
			showError('กรุณากรอกข้อมูล', 'กรุณากรอกรายละเอียด');
			return;
		}

		// ยืนยันก่อนบันทึก
		const confirmTitle = modalMode === 'add' ? 'ยืนยันการเพิ่มข้อมูล?' : 'ยืนยันการแก้ไข?';
		const confirmText = modalMode === 'add'
			? 'คุณต้องการเพิ่มกติกานี้หรือไม่?'
			: 'คุณต้องการบันทึกการแก้ไขหรือไม่?';

		const result = await showConfirm({
			title: confirmTitle,
			text: confirmText,
			confirmText: 'บันทึก',
			cancelText: 'ยกเลิก'
		});

		if (!result.isConfirmed) return;

		try {
			if (modalMode === 'add') {
				await axios.post(`${API_CONFIG.BASE_URL}/rules`, formData, {
					headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
				});

				showSuccess('สำเร็จ', 'เพิ่มข้อมูลเรียบร้อยแล้ว');
			} else {
				await axios.put(`${API_CONFIG.BASE_URL}/rules/${currentRule.id}`, formData, {
					headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
				});

				showSuccess('สำเร็จ', 'แก้ไขข้อมูลเรียบร้อยแล้ว');
			}

			setShowModal(false);
			fetchRules();
		} catch (error) {
			console.error('Error saving rule:', error);
			handleApiError(error);
		}
	};

	const handleInputChange = (e) => {
		const { name, value, type, checked } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: type === 'checkbox' ? checked : value
		}));
	};

	const handleDetailChange = (event, editor) => {
		const data = editor.getData();
		setFormData(prev => ({
			...prev,
			detail: data
		}));
	};

	const columns = [
		{
			key: 'actions',
			header: 'จัดการ',
			className: 'text-center',
			width: 80,
			render: (row) => (
				<div className="flex gap-2 justify-center">
					<button
						onClick={() => handleEdit(row)}
						className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition"
						title="แก้ไข"
					>
						{ICON_ACTION.EDIT}
					</button>
					<button
						onClick={() => handleDelete(row)}
						className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition"
						title="ลบ"
					>
						{ICON_ACTION.DELETE}
					</button>
				</div>
			)
		},
		{
			key: 'title',
			header: 'หัวข้อ',
			render: (row) => (
				<div className="max-w-md truncate" title={row.title}>
					{row.title}
				</div>
			)
		},
		{
		key: 'lotteryType',
		header: 'ประเภท',
		render: (row) => (
			<div className="text-center">
			<span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold min-w-36 ${
			row.type.toLowerCase() === 'powerball'
				? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
				: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
			}`}>
			{LOTTO_TYPE[row.type]}
			</span>
			</div>
		)
		},
		{
			key: 'status',
			header: 'สถานะ',
			width: 150,
			className: 'text-center',
			render: (row) => (
				<span className={`flex gap-2 justify-center px-2 py-1 rounded-full text-xs font-medium ${
					row.status
						? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
						: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
				}`}>
					{row.status ? 'ใช้งาน' : 'ไม่ใช้งาน'}
				</span>
			)
		},
		{
			key: 'updatedDate',
			header: 'วันที่แก้ไขล่าสุด',
			className: 'text-center',
			width: '170px',
			render: (row) => (
				<div className="flex justify-center">
					{formatDateTimeTable(row.updatedDate)}
				</div>
			)
		},
	];

	const toggleSearchForm = () => {
		setShowSearchForm(!showSearchForm);
	};

	const handleSearch = () => {
		setAppliedFilters(searchFilters);
		setCurrentPage(1);
	};

	const handleClearSearch = () => {
		const clearedFilters = {
			title: '',
			statusFilters: {
				active: true,
				inactive: true
			}
		};
		setSearchFilters(clearedFilters);
		setAppliedFilters(clearedFilters);
		setCurrentPage(1);
	};

	return (
		<Layout title="จัดการกติกาการเล่น" onLogout={onLogout} userRole={userRole}>
			{/* Header Actions */}
			<div className="mb-4 flex justify-between items-center">
				<div>
					<p className="text-gray-600 dark:text-gray-400">
						จัดการกติกาการเล่นทั้งหมด (สามารถใช้งานได้ประเภทละ 1 รายการ)
					</p>
				</div>
				<div className="flex items-center gap-3">
					<button
						onClick={toggleSearchForm}
						className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg transition shadow-lg ${
							showSearchForm
							? 'bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700'
							: 'bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700'
						}`}
					>
						{showSearchForm ? ICON_ACTION.CLOSE : ICON_ACTION.SEARCH}
						<span className='hidden md:inline'>ค้นหา</span>
					</button>
					<button
						onClick={handleAdd}
						className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
					>
						{ICON_ACTION.ADD}
						<span className='hidden md:inline'>เพิ่มกติกา</span>
					</button>
				</div>
			</div>

			{/* Search Filter Form - Show/Hide */}
			{showSearchForm && (
				<div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-4 mb-4 animate-fade-in">
					<div className="space-y-4 mb-4">
						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
								หัวข้อ
							</label>
							<input
								type="text"
								value={searchFilters.title}
								onChange={(e) => setSearchFilters({ ...searchFilters, title: e.target.value })}
								placeholder="ระบุชื่อหัวข้อ"
								className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
							/>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
								สถานะ
							</label>
							<div className="flex items-center gap-4">
								<label className="flex items-center space-x-2 cursor-pointer">
									<input
										type="checkbox"
										checked={searchFilters.statusFilters.active}
										onChange={(e) => setSearchFilters({
											...searchFilters,
											statusFilters: { ...searchFilters.statusFilters, active: e.target.checked }
										})}
										className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
									/>
									<span className="text-sm text-gray-700 dark:text-gray-300">ใช้งาน</span>
								</label>
								<label className="flex items-center space-x-2 cursor-pointer">
									<input
										type="checkbox"
										checked={searchFilters.statusFilters.inactive}
										onChange={(e) => setSearchFilters({
											...searchFilters,
											statusFilters: { ...searchFilters.statusFilters, inactive: e.target.checked }
										})}
										className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
									/>
									<span className="text-sm text-gray-700 dark:text-gray-300">ไม่ใช้งาน</span>
								</label>
							</div>
						</div>
					</div>
					<div className="flex justify-end gap-2">
						<button
							onClick={handleSearch}
							className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md font-medium transition-colors flex items-center gap-2"
						>
							{ICON_ACTION.SEARCH}
							ค้นหา
						</button>
						<button
							onClick={handleClearSearch}
							className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md font-medium transition-colors flex items-center gap-2"
						>
							{ICON_ACTION.CLEAR}
							ล้าง
						</button>
					</div>
				</div>
			)}

			{/* Data Table */}
			<DataTable
				columns={columns}
				data={rules}
				loading={loading}
				page={currentPage}
				pageSize={pageSize}
				totalItems={totalItems}
				onPageChange={setCurrentPage}
				onPageSizeChange={setPageSize}
				emptyMessage="ไม่พบข้อมูลกติกา"
			/>

			{/* Modal */}
			{showModal && (
				<div className="fixed top-0 inset-x-0 bg-black/50 flex items-top justify-center z-50 p-4 max-h-screen max-h-screen overflow-y-auto">
					<div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl h-full w-full max-w-full sm:max-w-4xl min-w-full sm:min-w-[1000px] my-5">
						<div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
							<h3 className="text-xl font-bold text-gray-900 dark:text-white">
								{modalMode === 'add' ? 'เพิ่มกติกาการเล่น' : 'แก้ไขกติกาการเล่น'}
							</h3>
							<button
								onClick={() => setShowModal(false)}
								className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
							>
								<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</div>

						<form onSubmit={handleSubmit} className="p-6 space-y-4">
							{/* Title */}
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
									หัวข้อ <span className="text-red-500">*</span>
								</label>
								<input
									type="text"
									name="title"
									value={formData.title}
									onChange={handleInputChange}
									className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
									required
									placeholder="ระบุหัวข้อกติกา"
								/>
							</div>

							{/* Type */}
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
									ประเภท <span className="text-red-500">*</span>
								</label>
								<div className="flex items-center">
								{Object.keys(LOTTO_TYPE).map(type => (
									<>
										<label className="flex content-center">
										<input
											type="radio"
											name="type"
											value={type}
											checked={formData.type === type}
											onChange={handleInputChange}
										/>
										<span className="text-sm font-medium text-gray-700 dark:text-gray-300 ml-2 mr-3">{LOTTO_TYPE[type]}</span>
										</label>
									</>
									))
								}
								</div>
							</div>

							{/* Detail - Rich Text Editor */}
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
									รายละเอียด <span className="text-red-500">*</span>
								</label>
								<div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600">
									<CKEditor
										editor={ClassicEditor}
										data={formData.detail}
										onChange={handleDetailChange}
									onReady={(editor) => {
										console.log('CKEditor is ready!', editor);
									}}
									config={{
										plugins: [
											Essentials, Heading,
											Bold, Italic, Underline, Strikethrough,
											FontColor, FontBackgroundColor,
											List, Link,
											Image, ImageCaption, ImageStyle, ImageToolbar, ImageUpload,
											BlockQuote, Paragraph,
										],
										licenseKey: 'GPL',
										extraPlugins: [CustomUploadAdapterPlugin],
										toolbar: [
											'heading', '|',
											'bold', 'italic', 'underline', 'strikethrough', '|',
											'fontColor', 'fontBackgroundColor', '|',
											'bulletedList', 'numberedList', '|',
											'link', 'imageUpload', '|',
											'blockQuote', '|',
											'undo', 'redo'
										],
										placeholder: 'ระบุรายละเอียดกติกาการเล่น...',
										image: {
											toolbar: [
												'imageStyle:inline',
												'imageStyle:block',
												'imageStyle:side',
												'|',
												'toggleImageCaption',
												'imageTextAlternative'
											]
										}
										}}
									/>
								</div>
							</div>

							{/* Status */}
							<div>
								<label className="flex items-center space-x-2 cursor-pointer">
									<input
										type="checkbox"
										name="status"
										checked={formData.status}
										onChange={handleInputChange}
										className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
									/>
									<span className="text-sm font-medium text-gray-700 dark:text-gray-300">
										ใช้งาน
									</span>
								</label>
							</div>

							{/* Form Actions */}
							<div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
								<button
									type="button"
									onClick={() => setShowModal(false)}
									className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition"
								>
									ยกเลิก
								</button>
								<button
									type="submit"
									className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
								>
									บันทึก
								</button>
							</div>
						</form>
					</div>
				</div>
			)}

			{/* Custom styles for CKEditor in dark mode */}
			<style jsx global>{`
				.ck-editor__editable {
					min-height: 250px;
				}

				.dark .ck.ck-editor__main > .ck-editor__editable {
					background-color: #1F2937;
					color: #F9FAFB;
					border-color: #4B5563;
				}

				.dark .ck.ck-toolbar {
					background-color: #374151;
					border-color: #4B5563;
				}

				.dark .ck.ck-button:not(.ck-disabled):hover,
				.dark .ck.ck-button:not(.ck-disabled):active {
					background-color: #1F2937;
				}

				.dark .ck.ck-button .ck-button__label {
					color: #E5E7EB;
				}

				.dark .ck.ck-icon {
					color: #9CA3AF;
				}

				.dark .ck.ck-dropdown__panel {
					background-color: #374151;
					border-color: #4B5563;
				}

				.dark .ck.ck-list__item .ck-button {
					color: #E5E7EB;
				}

				.dark .ck.ck-list__item .ck-button:hover {
					background-color: #1F2937;
				}
			`}</style>
		</Layout>
	);
};

export default Manage;
